import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { PasswordInput } from '@/components/ui/password-input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { BookOpen, Scale, Search, Sparkles, FolderOpen, Mail, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';

export default function LandingPage() {
  const navigate = useNavigate();
  const { login, register, user, loading: authLoading, verifyEmail, resendVerification } = useAuth();
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [showVerification, setShowVerification] = useState(false);
  const [verificationEmail, setVerificationEmail] = useState('');
  const [verificationCode, setVerificationCode] = useState('');

  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [registerData, setRegisterData] = useState({ email: '', password: '', full_name: '', referral_token: '' });

  // Redirect authenticated users to library
  useEffect(() => {
    if (user && !authLoading) {
      navigate('/library');
    }
  }, [user, authLoading]); // Removed navigate from dependencies to prevent infinite loop

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // If verification code is shown and filled, verify email first
      if (showVerification && verificationCode.length === 6) {
        await verifyEmail(verificationEmail, verificationCode);
        toast.success('Email verified! Logging you in...');
        setShowVerification(false);
        setVerificationCode('');
        // Now login
        const response = await login(loginData.email, loginData.password);
        if (response.requires_subscription) {
          toast.warning('Your trial has expired. Please subscribe to continue.');
          navigate('/subscribe');
        } else {
          toast.success('Welcome back!');
          navigate('/library');
        }
      } else {
        // Normal login
        const response = await login(loginData.email, loginData.password);
        
        if (response.requires_subscription) {
          toast.warning('Your trial has expired. Please subscribe to continue.');
          navigate('/subscribe');
        } else {
          toast.success('Welcome back!');
          navigate('/library');
        }
      }
    } catch (error) {
      // Handle session limit error
      if (error.isSessionLimitError) {
        toast.error(error.message, { duration: 6000 });
      } 
      // Handle email not verified error - show verification field
      else if (error.isEmailNotVerified) {
        setVerificationEmail(loginData.email);
        setShowVerification(true);
        toast.info('Please enter the verification code sent to your email.', { duration: 5000 });
      } else {
        toast.error(error.response?.data?.detail || 'Login failed');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await register(registerData.email, registerData.password, registerData.full_name, registerData.referral_token);
      
      // Check if email verification is required
      if (response.requires_verification) {
        setVerificationEmail(registerData.email);
        setShowVerification(true);
        setIsLogin(true); // Switch to Login tab
        setLoginData({ ...loginData, email: registerData.email }); // Pre-fill email
        toast.success('Registration successful! Please check your email for the verification code, then login.', { duration: 6000 });
      } else {
        toast.success('Account created successfully!');
        navigate('/library');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyEmail = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await verifyEmail(verificationEmail, verificationCode);
      toast.success('Email verified successfully! Welcome to LexProcure PH!');
      navigate('/library');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Verification failed. Please check your code.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    setLoading(true);
    try {
      await resendVerification(verificationEmail);
      toast.success('Verification code sent! Please check your email.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to resend code.');
    } finally {
      setLoading(false);
    }
  };

  // Show loading state while checking auth to prevent flicker
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-emerald-50 to-white">
        <div className="text-emerald-600">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <div className="flex-1 flex flex-col lg:flex-row">
        {/* Left Section - Branding with Skyline Background */}
        <div className="lg:w-1/2 relative overflow-hidden text-white p-8 lg:p-16 flex flex-col justify-center items-center">
        {/* Background with green overlay */}
        <div 
          className="absolute inset-0 z-0"
          style={{
            backgroundImage: 'url(/skyline_front.png)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat'
          }}
        >
          <div className="absolute inset-0" style={{ backgroundColor: 'rgba(0, 100, 50, 0.75)' }}></div>
        </div>

        {/* Content */}
        <div className="max-w-xl relative z-10 w-full">
          {/* Logo - Centered with Elegant Double-Line Gold Frame */}
          <div className="flex justify-center mb-8">
            <div className="relative p-3">
              {/* Outer gold border */}
              <div className="absolute inset-0 rounded-lg" style={{ 
                border: '2px solid #FFD700',
                boxShadow: '0 0 20px rgba(255, 215, 0, 0.4)'
              }}></div>
              {/* Inner gold border */}
              <div className="absolute inset-2 rounded-lg" style={{ 
                border: '1px solid #FFD700'
              }}></div>
              <img 
                src="/sleek-logo.png" 
                alt="LexProcure PH Logo" 
                className="h-56 lg:h-64 w-auto relative z-10"
              />
            </div>
          </div>

          <div className="mb-6 text-center">
            <h1 className="text-4xl lg:text-5xl font-bold mb-2" style={{ fontFamily: 'IBM Plex Sans' }}>
              <span style={{ color: '#FFD700' }}>Lex</span>
              <span className="text-white">Procure PH</span>
            </h1>
            <p className="text-lg lg:text-xl text-white">
              Your comprehensive digital library for Philippine government procurement laws
            </p>
          </div>

          <div className="space-y-6 mt-12">
            <div className="flex items-start gap-4 bg-white/10 backdrop-blur-sm p-4 rounded-lg border border-white/20">
              <div className="bg-emerald-400/30 p-3 rounded-lg">
                <Scale className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="font-semibold text-lg mb-1">Complete Legal Reference</h3>
                <p className="text-emerald-50 text-sm">Access RA 12009, RA 9184, their IRRs, relevant GPPB Resolutions and comparative matrices with full, searchable metadata so you always work from the latest and authoritative rules</p>
              </div>
            </div>

            <div className="flex items-start gap-4 bg-white/10 backdrop-blur-sm p-4 rounded-lg border border-white/20">
              <div className="bg-emerald-400/30 p-3 rounded-lg">
                <Sparkles className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="font-semibold text-lg mb-1">AI-Powered Assistant</h3>
                <p className="text-emerald-50 text-sm">Get instant answers to your Philippine procurement law questions from an AI built exclusively for this app and guided by a trusted GPPB Recognized Trainer; it does not just explain the rules, it also provides the legal requirements and offers practical considerations, so you learn how to apply the rules to real cases</p>
              </div>
            </div>

            <div className="flex items-start gap-4 bg-white/10 backdrop-blur-sm p-4 rounded-lg border border-white/20">
              <div className="bg-emerald-400/30 p-3 rounded-lg">
                <Search className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="font-semibold text-lg mb-1">Smart Tools</h3>
                <p className="text-emerald-50 text-sm">Use the Timeline Calculator, Section Equivalencies Finder (for counterpart provisions between RA 9184 and RA 12009), Mode Selection Wizard and Process Flows for ALL modes of procurement to turn complex rules into clear, step-by-step decisions</p>
              </div>
            </div>

            <div className="flex items-start gap-4 bg-white/10 backdrop-blur-sm p-4 rounded-lg border border-white/20">
              <div className="bg-emerald-400/30 p-3 rounded-lg">
                <FolderOpen className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="font-semibold text-lg mb-1">Personal Workspace</h3>
                <p className="text-emerald-50 text-sm">Save bookmarks and notes in your own organized workspace for fast recall of the provisions, rulings, and tools you use most</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right Section - Auth Forms */}
      <div className="lg:w-1/2 bg-white dark:bg-slate-900 p-8 lg:p-16 flex items-center justify-center">
        <div className="w-full max-w-md">
          {/* QR Code Section */}
          <div className="mb-8 text-center">
            <div className="flex justify-center mb-4">
              <img 
                src="/lexdoright-qr.png" 
                alt="LexProcure PH QR Code" 
                className="w-32 h-32 rounded-lg shadow-md"
              />
            </div>
            <p className="text-sm font-bold text-slate-700 dark:text-slate-300 leading-relaxed px-2">
              Developed by a GPPB Recognized Trainer, LexProcure PH reflects her years of hands-on work in Philippine government procurement, and is designed to help procuring entities, procurement professionals, and other stakeholders learn HOW TO APPLY the provisions with confidence and ease.
            </p>
          </div>

          <Tabs value={isLogin ? 'login' : 'register'} onValueChange={(v) => setIsLogin(v === 'login')}>
            <TabsList className="grid w-full grid-cols-2 mb-8 dark:bg-slate-800">
              <TabsTrigger value="login" data-testid="login-tab">Login</TabsTrigger>
              <TabsTrigger value="register" data-testid="register-tab">Register</TabsTrigger>
            </TabsList>

            <TabsContent value="login">
              <Card className="dark:bg-slate-800 dark:border-slate-700">
                <CardHeader>
                  <CardTitle className="dark:text-slate-100">Welcome Back</CardTitle>
                  <CardDescription className="dark:text-slate-400">Enter your credentials to access your library</CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleLogin} className="space-y-4">
                    <div>
                      <Label htmlFor="login-email" className="dark:text-slate-200">Email</Label>
                      <Input
                        id="login-email"
                        type="email"
                        data-testid="login-email-input"
                        placeholder="your@email.com"
                        value={loginData.email}
                        onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                        required
                        className="dark:bg-slate-700 dark:border-slate-600 dark:text-slate-100 dark:placeholder-slate-400"
                      />
                    </div>
                    <div>
                      <Label htmlFor="login-password" className="dark:text-slate-200">Password</Label>
                      <PasswordInput
                        id="login-password"
                        data-testid="login-password-input"
                        placeholder="••••••••"
                        value={loginData.password}
                        onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                        required
                        className="dark:bg-slate-700 dark:border-slate-600 dark:text-slate-100"
                      />
                    </div>
                    
                    {/* One-time Email Verification Code - shows when email not verified */}
                    {showVerification && (
                      <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                        <Label htmlFor="verification-code" className="dark:text-slate-200 flex items-center gap-2">
                          <Mail className="w-4 h-4 text-amber-600" />
                          One-Time Verification Code
                        </Label>
                        <Input
                          id="verification-code"
                          type="text"
                          data-testid="verification-code-input"
                          placeholder="Enter 6-digit code from email"
                          value={verificationCode}
                          onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                          className="mt-1 text-center text-lg tracking-widest dark:bg-slate-700 dark:border-slate-600 dark:text-white"
                          maxLength={6}
                        />
                        <p className="text-xs text-amber-700 dark:text-amber-400 mt-1">
                          Check your email ({verificationEmail}) for the verification code
                        </p>
                        <button
                          type="button"
                          onClick={handleResendVerification}
                          disabled={loading}
                          className="text-xs text-emerald-600 hover:text-emerald-700 dark:text-emerald-400 mt-1"
                        >
                          Didn't receive? Resend code
                        </button>
                      </div>
                    )}
                    
                    <div className="text-right">
                      <button
                        type="button"
                        onClick={() => navigate('/forgot-password')}
                        className="text-sm text-emerald-600 dark:text-emerald-400 hover:text-emerald-700 dark:hover:text-emerald-300 hover:underline"
                      >
                        Forgot Password?
                      </button>
                    </div>
                    <Button
                      type="submit"
                      className="w-full bg-emerald-600 hover:bg-emerald-700"
                      data-testid="login-submit-btn"
                      disabled={loading}
                    >
                      {loading ? (showVerification ? 'Verifying...' : 'Logging in...') : (showVerification ? 'Verify & Login' : 'Login')}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="register">
              <Card className="dark:bg-slate-800 dark:border-slate-700">
                <CardHeader>
                  <CardTitle className="dark:text-slate-100">Create Account</CardTitle>
                  <CardDescription className="dark:text-slate-400">Start exploring procurement laws today</CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleRegister} className="space-y-4">
                    <div>
                      <Label htmlFor="register-name" className="dark:text-slate-200">Full Name</Label>
                      <Input
                        id="register-name"
                        type="text"
                        data-testid="register-name-input"
                        placeholder="Juan Dela Cruz"
                        value={registerData.full_name}
                        onChange={(e) => setRegisterData({ ...registerData, full_name: e.target.value })}
                        required
                        className="dark:bg-slate-700 dark:border-slate-600 dark:text-slate-100 dark:placeholder-slate-400"
                      />
                    </div>
                    <div>
                      <Label htmlFor="register-email" className="dark:text-slate-200">Email</Label>
                      <Input
                        id="register-email"
                        type="email"
                        data-testid="register-email-input"
                        placeholder="your@email.com"
                        value={registerData.email}
                        onChange={(e) => setRegisterData({ ...registerData, email: e.target.value })}
                        required
                        className="dark:bg-slate-700 dark:border-slate-600 dark:text-slate-100 dark:placeholder-slate-400"
                      />
                    </div>
                    <div>
                      <Label htmlFor="register-password" className="dark:text-slate-200">Password</Label>
                      <PasswordInput
                        id="register-password"
                        data-testid="register-password-input"
                        placeholder="••••••••"
                        value={registerData.password}
                        onChange={(e) => setRegisterData({ ...registerData, password: e.target.value })}
                        required
                        className="dark:bg-slate-700 dark:border-slate-600 dark:text-slate-100"
                      />
                    </div>
                    <div>
                      <Label htmlFor="referral-token" className="dark:text-slate-200">
                        Referral Token <span className="text-slate-500 text-xs font-normal">(Optional)</span>
                      </Label>
                      <Input
                        id="referral-token"
                        type="text"
                        data-testid="referral-token-input"
                        placeholder="LEX-XXXXXXXXX"
                        value={registerData.referral_token}
                        onChange={(e) => setRegisterData({ ...registerData, referral_token: e.target.value.toUpperCase() })}
                        className="dark:bg-slate-700 dark:border-slate-600 dark:text-slate-100 dark:placeholder-slate-400 font-mono"
                        maxLength={13}
                      />
                      <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                        Have a referral token? Enter it here to support your referrer
                      </p>
                    </div>
                    <Button
                      type="submit"
                      className="w-full bg-emerald-600 hover:bg-emerald-700"
                      data-testid="register-submit-btn"
                      disabled={loading}
                    >
                      {loading ? 'Creating account...' : 'Create Account'}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </div>
      </div>

      {/* Footer */}
      <footer className="bg-slate-900 text-slate-400 py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm">© 2025 LexProcurePH. All rights reserved.</p>
            <div className="flex gap-6 text-sm">
              <button onClick={() => navigate('/terms')} className="hover:text-white transition-colors">Terms & Conditions</button>
              <button onClick={() => navigate('/privacy')} className="hover:text-white transition-colors">Privacy Policy</button>
              <button onClick={() => navigate('/refund-policy')} className="hover:text-white transition-colors">Refund Policy</button>
            </div>
            <p className="text-xs text-slate-500">Powered by LexDoRight</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
